import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import Autoplay from 'embla-carousel-autoplay';

const TestimonialsSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 6000, stopOnInteraction: false })
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const reviews = [
    {
      name: "Sarah M.",
      location: "Winter Park, FL",
      rating: 5,
      text: "I've been avoiding my garage for YEARS. These two came in and transformed it in a few hours. Now I actually park my car inside! They even labeled everything so I can find my stuff. Absolute lifesavers!",
      service: "2-car garage organization"
    },
    {
      name: "Mike T.",
      location: "Orlando, FL",
      rating: 5,
      text: "The sibling duo is fantastic! They're professional, personable, and made the whole experience stress-free. My garage went from disaster zone to organized paradise. Worth every penny!",
      service: "Full garage cleaning"
    },
    {
      name: "Jennifer L.",
      location: "Maitland, FL",
      rating: 5,
      text: "I called them for a 'quick declutter' and they discovered I had like 5 of everything. They helped me donate tons of stuff and now my garage is actually functional. Plus they're just really nice people!",
      service: "1-car garage declutter"
    },
    {
      name: "Amanda K.",
      location: "Altamonte Springs, FL",
      rating: 5,
      text: "These two are the real deal. Showed up on time, worked efficiently, and didn't judge my chaos (which was bad, trust me). My garage has never looked this good. 10/10 would hire again!",
      service: "3-car garage with pressure washing"
    }
  ];

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section id="reviews" className="py-20 px-4 bg-slate-900 text-white scroll-mt-20">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-600/20 backdrop-blur-sm border border-emerald-500/30 text-emerald-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Star className="h-4 w-4 fill-current" />
            <span>5-Star Rated Service</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            What Our Customers Say
          </h2>
          <p className="text-xl text-slate-300">
            Real reviews from real Orlando-area garages
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {reviews.map((review, index) => (
                <div key={index} className="flex-[0_0_100%] min-w-0 px-4">
                  <div className="bg-white/10 backdrop-blur-md p-8 md:p-12 rounded-2xl border border-white/20 mx-auto max-w-3xl">
                    <Quote className="h-12 w-12 text-emerald-400 mb-4" />
                    
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>

                    <p className="text-white text-lg md:text-xl leading-relaxed mb-6 italic">
                      "{review.text}"
                    </p>

                    <div className="flex items-center justify-between border-t border-white/20 pt-6">
                      <div>
                        <p className="font-bold text-white text-lg">{review.name}</p>
                        <p className="text-emerald-300 text-sm">{review.location}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-300 text-sm">{review.service}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 text-white p-3 rounded-full transition-all hover:scale-110"
            aria-label="Previous review"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 text-white p-3 rounded-full transition-all hover:scale-110"
            aria-label="Next review"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <div className="flex justify-center gap-2 mt-8">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => emblaApi && emblaApi.scrollTo(index)}
                className={`h-2 rounded-full transition-all ${
                  index === selectedIndex
                    ? 'w-8 bg-emerald-500'
                    : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;